import styled, { css } from 'styled-components'
import type { DotsVariant } from './types'

interface DotsProps {
    count: number
    selectedIndex: number
    variant?: DotsVariant
    onDotClick?: (index: number) => void
}

const MAX_VISIBLE_DOTS = 5

function getVisibleRange(count: number, selected: number): { start: number; end: number } {
    const half = Math.floor(MAX_VISIBLE_DOTS / 2)
    let start = Math.max(0, selected - half)
    const end = Math.min(count - 1, start + MAX_VISIBLE_DOTS - 1)
    start = Math.max(0, end - MAX_VISIBLE_DOTS + 1)
    return { start, end }
}

const DotsContainer = styled.div<{ $variant: DotsVariant }>`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 8px 0;
    z-index: 1;

    ${({ $variant }) =>
        $variant === 'gradient' &&
        css`
            background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.64) 100%);
            padding-bottom: 10px;
        `}
`

const Dot = styled.button<{ $active: boolean; $small?: boolean }>`
    width: ${({ $active, $small }) => ($active ? '8px' : $small ? '4px' : '6px')};
    height: ${({ $active, $small }) => ($active ? '8px' : $small ? '4px' : '6px')};
    border-radius: 50%;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.5)')};
    flex-shrink: 0;
`

export function EmblaCarouselDots({ count, selectedIndex, variant = 'dark', onDotClick }: DotsProps) {
    if (count <= 1) return null

    const { start, end } = getVisibleRange(count, selectedIndex)
    const visibleDots = Array.from({ length: count }, (_, i) => i).filter(i => i >= start && i <= end)

    return (
        <DotsContainer $variant={variant}>
            {visibleDots.map(i => (
                <Dot
                    key={i}
                    $active={i === selectedIndex}
                    $small={i === start && start > 0}
                    onClick={() => onDotClick?.(i)}
                    aria-label={`Go to slide ${i + 1}`}
                />
            ))}
        </DotsContainer>
    )
}
